import { FunctionComponent, useMemo } from "react";

type Option = string | { label: string; value: string };

type Props = {
  title?: string;
  label?: string;
  ariaLabel?: string;
  name?: string;
  className?: string;
  wrapperClassName?: string;
  options: Array<Option>;
  placeholder?: string;
  value?: string;
  onChange?: (e: React.FormEvent<HTMLSelectElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLSelectElement, Element>) => void;
  errorMsg?: string;
  valid?: boolean;
  disabled?: boolean;
  autocomplete?: string;
  hint?: string;
};

const FormSelect: FunctionComponent<Props> = (props: Props) => {
  const {
    title,
    label,
    ariaLabel,
    name,
    className = "",
    wrapperClassName = "",
    options,
    placeholder = "Please select",
    value,
    onChange,
    onBlur,
    errorMsg,
    valid,
    disabled,
    autocomplete,
    hint,
  } = props;

  const hasError = !!errorMsg;
  const isValid = !!valid && !hasError;
  const isEmpty = !value;

  const stateStyle: React.CSSProperties = useMemo(() => {
    if (hasError) return { borderColor: "#dc2626" };
    if (isValid) return { borderColor: "#16a34a" };
    return { borderColor: "#d1d5db" };
  }, [hasError, isValid]);

  const focusRing = hasError
    ? "focus:outline-none focus:ring-4 focus:ring-red-100 focus:border-red-500"
    : isValid
    ? "focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500"
    : "focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500";

  return (
    <div className={`${wrapperClassName} ${className}`}>
      {title && <p className="mb-2 text-left">{title}</p>}
      {label ? (
        <label
          htmlFor={name}
          className="block text-sm font-bold mb-1 text-left"
          style={{ color: "#0f1111" }}
        >
          {label}
        </label>
      ) : null}
      <div className="relative">
        <select
          id={name}
          name={name}
          value={value ?? ""}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          autoComplete={autocomplete}
          aria-label={ariaLabel}
          aria-invalid={hasError || undefined}
          className={`block w-full rounded-xl bg-white text-base pl-3 pr-8 py-2 border appearance-none disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors cursor-pointer ${focusRing}`}
          style={{
            color: isEmpty ? "#9ca3af" : "#111827",
            ...stateStyle,
          }}
        >
          <option value="" disabled hidden>
            {placeholder}
          </option>
          {options.map((opt) => {
            const o =
              typeof opt === "string" ? { label: opt, value: opt } : opt;
            return (
              <option
                key={o.value}
                value={o.value}
                style={{ color: "#111827" }}
              >
                {o.label}
              </option>
            );
          })}
        </select>
        <svg
          className="absolute right-3 top-1/2 w-5 h-5 pointer-events-none"
          style={{ transform: "translateY(-50%)", color: "#6b7280" }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>
      {hasError ? (
        <p
          className="mt-1 text-xs text-left"
          style={{ color: "#dc2626" }}
        >
          {errorMsg}
        </p>
      ) : hint ? (
        <p
          className="mt-1 text-xs text-left"
          style={{ color: "#565959" }}
        >
          {hint}
        </p>
      ) : null}
    </div>
  );
};

export { FormSelect, FormSelect as default };
